import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import api from '../../utils/functions/api';
import { confirmAsync } from '../../utils/functions/globalDialog';
import { useSocket } from '../../contexts/SocketContext';
import RecurringTable from './expensesRecurring/RecurringTable';
import RecurringFormModal from './expensesRecurring/dialogs/RecurringFormModal';
import RecurringHistoryDialog from './expensesRecurring/dialogs/RecurringHistoryDialog';
import CreateExpenseFromRecurringModal from './expensesRecurring/dialogs/CreateExpenseFromRecurringModal';

// หน้า "จัดการรายการเบิกประจำ" (เมนูแยกใน sidebar การเงิน, route /finance/expenses/recurring) — สร้าง/แก้ไข/ลบ
// template รายการเบิกที่เกิดซ้ำทุกเดือน/ทุกปี เก็บใน collection แยก (ExpensesRecurring) ไม่ปนกับ Expenses จริง
// ระบบไม่สร้างรายการเบิกให้เอง (ยอดแต่ละงวดไม่เท่ากัน + ต้องแนบหลักฐาน) — แค่เตือนเมื่อใกล้ถึงวันที่รับเงิน
// (backend คำนวณสถานะงวดมาให้ใน row.periods) แล้วให้กดปุ่มสร้างรายการเบิกของงวดนั้นเอง
// (CreateExpenseFromRecurringModal) รายการที่สร้างแล้วดูได้ที่แท็บ "รายการเบิกประจำ" ในหน้า /finance/expenses
//
// component นี้เป็นแค่ container: ถือ list + ควบคุมว่า modal/dialog ไหนเปิดอยู่ ตรรกะฟอร์ม/history
// อยู่ใน ./expensesRecurring/ ทั้งหมด
export default function ExpensesRecurringManage({ title }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // row ที่กำลังแก้ไข หรือ null = สร้างใหม่
  const [historyTarget, setHistoryTarget] = useState(null); // id ของ template ที่กำลังดูประวัติ
  const [createTarget, setCreateTarget] = useState(null); // { row, period } งวดที่กำลังสร้างรายการเบิก

  const fetchRows = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`${process.env.REACT_APP_API_URL}/expenses-recurring?size=200`);
      setRows(data?.rows || []);
    } catch (error) {
      console.error(error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // รายการเบิกถูกสร้าง/ยกเลิกจากที่อื่น (คนอื่นกดสร้างงวดเดียวกัน, ยกเลิกรายการในหน้ารายการเบิก) — สถานะงวดเปลี่ยน
  // debounce + refetch เงียบๆ (ไม่โชว์ skeleton) เพราะ event พวกนี้ broadcast ทั้งระบบ ยิงถี่ได้มาก (เช่น HR แก้
  // ตารางเงินเดือน sync ค่าแรงทุก 2.5 วิ)
  const socket = useSocket();
  const refreshTimerRef = useRef(null);
  useEffect(() => {
    if (!socket) return undefined;
    const refresh = () => {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => fetchRows({ silent: true }), 3000);
    };
    socket.on('expenses:created', refresh);
    socket.on('expenses:updated', refresh);
    return () => {
      clearTimeout(refreshTimerRef.current);
      socket.off('expenses:created', refresh);
      socket.off('expenses:updated', refresh);
    };
  }, [socket, fetchRows]);

  const handleOpenCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditing(row);
    setFormOpen(true);
  };

  const handleDelete = async (row) => {
    const confirm = await confirmAsync(`ลบรายการเบิกประจำ "${row.name}"?`);
    if (!confirm) return;
    try {
      await api.delete(`${process.env.REACT_APP_API_URL}/expenses-recurring/${row._id}`);
      await fetchRows();
    } catch (error) {
      console.error(error);
    }
  };

  // สลับเปิด/ปิด — ปิดไว้ = ไม่เตือนงวดถัดไป (ไม่ลบ template ทิ้ง)
  const handleToggleActive = async (row) => {
    try {
      await api.put(`${process.env.REACT_APP_API_URL}/expenses-recurring/${row._id}`, { active: row.active === false });
      await fetchRows();
    } catch (error) {
      console.error(error);
    }
  };

  const pendingPeriods = rows.flatMap((r) => (r.periods || []).filter((p) => p.status === 'due' || p.status === 'overdue'));
  const overdueCount = pendingPeriods.filter((p) => p.status === 'overdue').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>{title || 'รายการเบิกประจำ'}</Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          เพิ่มรายการเบิกประจำ
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        ตั้งแม่แบบรายการเบิกที่เกิดซ้ำทุกเดือน/ทุกปี — ใกล้ถึงวันที่รับเงินระบบจะเตือนให้กดสร้างรายการเบิก
        (เตรียมโครงการ/งบประมาณ/รายการ/ผู้รับเงินไว้ให้ กรอกยอดและแนบหลักฐานเอง) ดูรายการที่สร้างแล้วได้ที่แท็บ
        "รายการเบิกประจำ" ในหน้ารายการเบิกเงิน
      </Typography>

      {pendingPeriods.length > 0 && (
        <Alert severity={overdueCount > 0 ? 'error' : 'warning'} sx={{ mb: 2 }}>
          มี {pendingPeriods.length} งวดที่ต้องสร้างรายการเบิก
          {overdueCount > 0 ? ` (เลยกำหนดแล้ว ${overdueCount} งวด)` : ''} — กดปุ่ม "สร้าง" ในคอลัมน์ "งวดที่ต้องสร้าง"
        </Alert>
      )}

      <RecurringTable
        rows={rows}
        loading={loading}
        onEdit={handleOpenEdit}
        onDelete={handleDelete}
        onHistory={(row) => setHistoryTarget(row._id)}
        onToggleActive={handleToggleActive}
        onCreate={(row, period) => setCreateTarget({ row, period })}
      />

      <RecurringFormModal
        open={formOpen}
        editing={editing}
        onClose={() => setFormOpen(false)}
        onSaved={() => fetchRows()}
      />

      <RecurringHistoryDialog
        templateId={historyTarget}
        onClose={() => setHistoryTarget(null)}
      />

      <CreateExpenseFromRecurringModal
        target={createTarget}
        onClose={() => setCreateTarget(null)}
        onCreated={() => fetchRows({ silent: true })}
      />
    </Box>
  );
}
